import {Component, HostListener, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    FormArray
} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {
    TaskService,
    TaskDetailResponse,
    UpdateTaskDetailsRequest
} from '@services/task.service';
import {AddTaskComponent} from '@components/task/add-task/add-task.component';
import {
    FilterConfig,
    SearchFilterComponent
} from '@components/search-filter/search-filter.component';
import {FarmEnvironmentService} from '@services/farm-environment.service';
import {PlacementService} from '@services/placement.service';
import {FarmUserServiceService} from '@services/farm-user-service.service';
import {DialogModule} from 'primeng/dialog';
import {ToastrService} from 'ngx-toastr';
import {PaginatorModule} from 'primeng/paginator';
import {TableModule} from 'primeng/table';
import {TooltipModule} from 'primeng/tooltip';
@Component({
    selector: 'app-tasks-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        DialogModule,
        PaginatorModule,
        TableModule,
        TooltipModule,
        AddTaskComponent,
        SearchFilterComponent
    ],
    templateUrl: './tasks-list.component.html',
    styleUrl: './tasks-list.component.scss'
})
export class TasksListComponent implements OnInit {
    private static readonly DEFAULT_STATUS_ID = 1;
    tasks: any[] = [];
    editTaskForm!: FormGroup;
    showAddDialog = false;
    showEditDialog = false;
    showViewDialog = false;
    loading = false;
    editTaskData: TaskDetailResponse | null = null;
    viewTaskData: TaskDetailResponse | null = null;
    // filters
    title = '';
    assigneeId: number | null = null;
    categoryId: number | null = null;
    statusId: number | null = null;
    farmId: number | null = null;
    houseId: number | null = null;
    cycleId: number | null = null;
    dueDate: string | null = null;

    activeActionMenu: string | null = null;
    public userDetail: any;
    public filterForm: FormGroup;
    public taskListFilterForm: FormGroup;
    public filteringData: any = null;

    public taskFilterConfig: FilterConfig = {
        showFarmFilter: false,
        showHouseFilter: false,
        fields: [
            // Farm hierarchy (cascade)
            {key: 'farmId', label: 'Farm', type: 'select', options: []},
            {key: 'houseId', label: 'House', type: 'select', options: []},
            {key: 'cycleId', label: 'Cycle', type: 'select', options: []},
            // Task attributes
            {key: 'assigneeId', label: 'Assignee', type: 'select', options: []},
            {key: 'dueDate', label: 'Due Date', type: 'date', options: []},
            {key: 'category', label: 'Category', type: 'select', options: []},
            {key: 'status', label: 'Status', type: 'select', options: []}
        ]
    };
    public assigneeOptions: any[] = [];
    public dueDateOptions: any[] = [];
    public cycleOptions: any[] = [];
    public farmOptions: any[] = [];
    public houseOptions: any[] = [];
    public partitionOptions: any[] = [];
    public categoryOptions: any[] = [];
    public statusOptions: any[] = [];
    // Guard to ignore stale responses
    private lastRequestId = 0;

    // Attachment handling for edit form
    mainAttachmentFile: File | null = null;
    subTaskAttachmentFiles: {[index: number]: File | null} = {};

    totalRecords: number = 0;
    pageSize: number = 10;
    currentPage: number = 1;

    constructor(
        private taskService: TaskService,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private farmEnvironmentService: FarmEnvironmentService,
        private placementService: PlacementService,
        private farmUserService: FarmUserServiceService
    ) {
        // Initialize edit form with empty values
        this.editTaskForm = this.fb.group({
            title: ['', Validators.required],
            assignee: [''],
            startDate: ['', Validators.required],
            dueDate: ['', Validators.required],
            category: [''],
            status: [''],
            description: [''],
            farmId: [null],
            houseId: [null],
            cycleId: [null],
            partitionId: [null],
            attachment: [''],
            subTasks: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.userDetail = JSON.parse(
            localStorage.getItem('UserDetail') || '{}'
        );
        this.initializeForms();
        this.setupCascadingFilters();
        this.loadFarmHierarchy();
        this.fetchTasks();
        this.loadTaskCategories();
        this.loadTaskStatuses();
        this.loadAssignees();
    }

    private loadTaskCategories() {
        this.taskService.getTaskCategories().subscribe({
            next: (result) => {
                const list = Array.isArray(result)
                    ? result
                    : (result as any)?.data || [];
                // Use ID as value
                this.categoryOptions = list.map((c: any) => ({
                    value: c.id,
                    label: c.name
                }));

                const categoryField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'category'
                );
                if (categoryField) categoryField.options = this.categoryOptions;
            },
            error: () => {
                this.categoryOptions = [];
                const categoryField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'category'
                );
                if (categoryField) categoryField.options = [];
            }
        });
    }

    private loadAssignees() {
        const currentUserId = this.userDetail?.id;
        if (!currentUserId) {
            // Clear options if no user
            this.assigneeOptions = [];
            const assigneeField = this.taskFilterConfig.fields.find(
                (f) => f.key === 'assigneeId'
            );
            if (assigneeField) assigneeField.options = [];
            return;
        }
        const name = '';
        const page = 1;
        const pageSize = 1000;

        this.farmUserService
            .getFarmUserList(currentUserId, name, page, pageSize)
            .subscribe({
                next: (result) => {
                    const farmUsers = Array.isArray(result)
                        ? result
                        : result?.farmUsers || [];
                    this.assigneeOptions = farmUsers.map((u: any) => ({
                        value: u.id,
                        label: u.name
                    }));
                    const assigneeField = this.taskFilterConfig.fields.find(
                        (f) => f.key === 'assigneeId'
                    );
                    if (assigneeField)
                        assigneeField.options = this.assigneeOptions;
                },
                error: () => {
                    this.assigneeOptions = [];
                    const assigneeField = this.taskFilterConfig.fields.find(
                        (f) => f.key === 'assigneeId'
                    );
                    if (assigneeField) assigneeField.options = [];
                }
            });
    }
    private loadTaskStatuses() {
        this.taskService.getTaskStatus().subscribe({
            next: (result) => {
                const list = Array.isArray(result)
                    ? result
                    : (result as any)?.data || [];
                // Use ID as value
                this.statusOptions = list.map((s: any) => ({
                    value: s.id,
                    label: s.name
                }));

                const statusField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'status'
                );
                if (statusField) statusField.options = this.statusOptions;
            },
            error: () => {
                this.statusOptions = [];
                const statusField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'status'
                );
                if (statusField) statusField.options = [];
            }
        });
    }

    private loadCycles() {
        const selectedFarmId = this.filterForm.get('farmId')?.value;
        const selectedHouseId = this.filterForm.get('houseId')?.value || 0;

        if (!selectedFarmId) {
            this.cycleOptions = [];
            const cycleField = this.taskFilterConfig.fields.find(
                (f) => f.key === 'cycleId'
            );
            if (cycleField) cycleField.options = [];
            return;
        }

        this.placementService
            .getCyclesByFarmAndHouse(selectedFarmId, selectedHouseId)
            .subscribe({
                next: (result) => {
                    const cycles = Array.isArray(result) ? result : [];

                    this.cycleOptions = cycles.map((cycle: any) => ({
                        value: cycle.id,
                        label: cycle.name
                    }));

                    const cycleField = this.taskFilterConfig.fields.find(
                        (f) => f.key === 'cycleId'
                    );
                    if (cycleField) {
                        cycleField.options = this.cycleOptions;
                    }
                },
                error: () => {
                    this.cycleOptions = [];
                    const cycleField = this.taskFilterConfig.fields.find(
                        (f) => f.key === 'cycleId'
                    );
                    if (cycleField) cycleField.options = [];
                }
            });
    }

    private loadFarmHierarchy() {
        if (this.userDetail?.id) {
            this.farmEnvironmentService
                .getdashboardFarmList(this.userDetail.id)
                .subscribe((result) => {
                    const farmData = Array.isArray(result)
                        ? result
                        : result?.farms || result || [];
                    this.buildFarmOptions(farmData);
                });
        }
    }

    private setupCascadingFilters() {
        this.filterForm.get('farmId')?.valueChanges.subscribe((farmId) => {
            this.filterForm.get('houseId')?.reset(null, {emitEvent: false});
            this.filterForm.get('cycleId')?.reset(null, {emitEvent: false});
            this.houseOptions = [];

            const houseField = this.taskFilterConfig.fields.find(
                (f) => f.key === 'houseId'
            );
            if (houseField) houseField.options = [];

            if (farmId) {
                this.loadHousesForFarm(farmId);
                this.loadCycles();
            } else {
                this.cycleOptions = [];
                const cycleField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'cycleId'
                );
                if (cycleField) cycleField.options = [];
            }
        });

        this.filterForm.get('houseId')?.valueChanges.subscribe((houseId) => {
            this.filterForm.get('cycleId')?.reset(null, {emitEvent: false});

            this.loadCycles();
        });
    }

    private initializeForms() {
        this.filterForm = this.fb.group({
            assigneeId: [null],
            dueDate: [null],
            category: [null],
            status: [null],
            farmId: [null],
            houseId: [null],
            partitionId: [null],
            cycleId: [null]
        });
    }

    onTaskFilterApplied(filterData: any) {
        // The filter form is external and already contains values; just refetch
        this.filteringData = this.filterForm.value;
        this.currentPage = 1;
        this.fetchTasks();
    }

    onTaskFilterCleared() {
        this.filterForm.reset({
            farmId: null,
            houseId: null,
            partitionId: null,
            cycleId: null,
            assigneeId: null,
            dueDate: null,
            category: null,
            status: null
        });
        if (this.taskListFilterForm) this.taskListFilterForm.reset();
        this.filteringData = null;

        // Clear dependent dropdowns
        this.houseOptions = [];
        this.cycleOptions = [];

        // Update filter config to clear house options
        const houseField = this.taskFilterConfig.fields.find(
            (f) => f.key === 'houseId'
        );
        if (houseField) {
            houseField.options = [];
        }

        const cycleField = this.taskFilterConfig.fields.find(
            (f) => f.key === 'cycleId'
        );
        if (cycleField) cycleField.options = [];

        this.currentPage = 1;
        this.fetchTasks();
    }

    private loadHousesForFarm(farmId: number) {
        this.farmEnvironmentService
            .getdashboardFarmHouseList(farmId)
            .subscribe((result) => {
                const houseData = Array.isArray(result)
                    ? result
                    : result.houses || result.farmHouses || result || [];
                this.houseOptions = houseData.map((house: any) => ({
                    value: house.id,
                    label: house.name
                }));

                // Update house options in filter config
                const houseField = this.taskFilterConfig.fields.find(
                    (f) => f.key === 'houseId'
                );
                if (houseField) {
                    houseField.options = this.houseOptions;
                }
            });
    }

    // Build farm options from the API data
    private buildFarmOptions(farmData: any[]) {
        this.farmOptions = farmData.map((farm) => ({
            value: farm.id,
            label: farm.name
        }));

        const farmField = this.taskFilterConfig.fields.find(
            (f) => f.key === 'farmId'
        );
        if (farmField) {
            farmField.options = this.farmOptions;
        }
    }

    // Add Task dialog controls
    openAddTask() {
        this.showAddDialog = true;
    }

    handleCloseAddTask() {
        this.showAddDialog = false;
    }

    handleTaskSaved(savedTask: any) {
        this.showAddDialog = false;
        this.fetchTasks();
    }

    handleEditSaved(updatedTask: any) {
        this.showEditDialog = false;

        const idx = this.tasks.findIndex((t) => t.id === updatedTask.id);
        if (idx > -1) {
            this.tasks[idx] = {...this.tasks[idx], ...updatedTask};
        }
    }

    // Open View Details modal using TaskDetailById API
    openViewDetails(task: any) {
        this.loading = true;
        this.taskService.getTaskDetailById(task.id).subscribe({
            next: (taskDetails: TaskDetailResponse) => {
                this.viewTaskData = taskDetails;
                this.showViewDialog = true;
                this.loading = false;
            },
            error: (error: any) => {
                console.error('Error loading task details:', error);
                this.toastr.error('Failed to load task details');
                this.loading = false;
            }
        });
    }

    // Open Edit Task modal using TaskDetailById API
    openEditTask(task: any) {
        this.loading = true;
        this.taskService.getTaskDetailById(task.id).subscribe({
            next: (taskDetails: TaskDetailResponse) => {
                this.editTaskData = taskDetails;
                this.initializeEditForm(taskDetails);
                this.showEditDialog = true;
                this.loading = false;
            },
            error: (error: any) => {
                this.toastr.error('Failed to load task for editing');
                this.loading = false;
            }
        });
    }

    // Initialize edit form with task data
    private initializeEditForm(taskDetails: TaskDetailResponse) {
        // Find the assignee, category, and status values for the dropdowns
        const assigneeValue = taskDetails.assigneeId
            ? this.getAssigneeNameById(taskDetails.assigneeId)
            : taskDetails.assignee || '';

        const categoryValue = taskDetails.categoryId
            ? this.getCategoryNameById(taskDetails.categoryId)
            : taskDetails.category || '';

        const statusValue = taskDetails.statusId
            ? this.getStatusNameById(taskDetails.statusId)
            : taskDetails.status || '';
        // Build subTasks form array
        const subTaskControls = (taskDetails.subTasks || []).map((st) =>
            this.fb.group({
                id: [st.id],
                title: [st.title, Validators.required],
                assignee: [st.assignee || ''],
                startDate: [this.formatDateForInput(st.startDate)],
                dueDate: [this.formatDateForInput(st.dueDate)],
                status: [st.status || ''],
                subTaskAttachment: [st.subTaskAttachment || ''],
                description: [st.description || '']
            })
        );
        const subTasksFA = this.fb.array(subTaskControls);

        this.editTaskForm = this.fb.group({
            title: [taskDetails.title, Validators.required],
            assignee: [assigneeValue],
            startDate: [
                this.formatDateForInput(taskDetails.startDate),
                Validators.required
            ],
            dueDate: [
                this.formatDateForInput(taskDetails.dueDate),
                Validators.required
            ],
            category: [categoryValue],
            status: [statusValue],
            description: [''],
            farmId: [taskDetails.farmId || null],
            houseId: [taskDetails.houseId || null],
            cycleId: [taskDetails.cycleId || null],
            partitionId: [taskDetails.partitionId || null],
            attachment: [''],
            subTasks: subTasksFA
        });

        // Reset selected files for attachments when opening edit
        this.mainAttachmentFile = null;
        this.subTaskAttachmentFiles = {};

        // Load dependent options for edit context
        if (taskDetails.farmId) {
            this.loadHousesForFarm(taskDetails.farmId);
            this.loadCyclesForEdit(
                taskDetails.farmId,
                taskDetails.houseId || 0
            );
        }
        if (taskDetails.houseId) {
            this.loadPartitionsForHouse(taskDetails.houseId);
        }

        // Cascading behavior in edit form
        this.editTaskForm.get('farmId')?.valueChanges.subscribe((farmId) => {
            this.editTaskForm.get('houseId')?.reset(null, {emitEvent: false});
            this.editTaskForm.get('cycleId')?.reset(null, {emitEvent: false});
            this.editTaskForm
                .get('partitionId')
                ?.reset(null, {emitEvent: false});
            if (farmId) {
                this.loadHousesForFarm(farmId);
                this.loadCyclesForEdit(farmId, 0);
            }
        });

        this.editTaskForm.get('houseId')?.valueChanges.subscribe((houseId) => {
            this.editTaskForm.get('cycleId')?.reset(null, {emitEvent: false});
            this.editTaskForm
                .get('partitionId')
                ?.reset(null, {emitEvent: false});
            const farmId = this.editTaskForm.get('farmId')?.value;
            if (farmId) {
                this.loadCyclesForEdit(farmId, houseId || 0);
            }
            if (houseId) this.loadPartitionsForHouse(houseId);
        });
    }

    get subTasksFA(): FormArray {
        return this.editTaskForm.get('subTasks') as FormArray;
    }

    addSubTask() {
        if (!this.editTaskForm) return;
        this.subTasksFA.push(
            this.fb.group({
                id: [0],
                title: ['', Validators.required],
                assignee: [''],
                startDate: [this.formatDateForInput(new Date().toISOString())],
                dueDate: [this.formatDateForInput(new Date().toISOString())],
                status: [''],
                subTaskAttachment: [''],
                description: ['']
            })
        );
    }

    removeSubTask(index: number) {
        if (!this.editTaskForm) return;
        if (index > -1 && index < this.subTasksFA.length) {
            this.subTasksFA.removeAt(index);
        }
    }

    // Format date for datetime-local input
    private formatDateForInput(dateString: string): string {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Update task using API
    async updateTask() {
        if (!this.editTaskForm.valid || !this.editTaskData) {
            return;
        }

        const formValue = this.editTaskForm.value as any;
        const mainAttachmentBase64 = this.mainAttachmentFile
            ? await this.getFileBase64(this.mainAttachmentFile)
            : '';
        const startDateIso = new Date(formValue.startDate).toISOString();
        const dueDateIso = new Date(formValue.dueDate).toISOString();
        const updateRequest: UpdateTaskDetailsRequest = {
            id: this.editTaskData.id,
            title: formValue.title,
            assigneeId: this.getAssigneeIdFromName(formValue.assignee),
            farmId:
                formValue.farmId ??
                this.editTaskData.farmId ??
                this.getFarmIdFromName(this.editTaskData.farm || ''),
            farmHouseId:
                formValue.houseId ??
                this.editTaskData.houseId ??
                this.getHouseIdFromName(this.editTaskData.house || ''),
            partitionId:
                formValue.partitionId ??
                this.editTaskData.partitionId ??
                this.getPartitionIdFromName(this.editTaskData.partition || ''),
            cycleId:
                formValue.cycleId ??
                this.editTaskData.cycleId ??
                this.getCycleIdFromName(this.editTaskData.cycle || ''),
            startDate: startDateIso,
            dueDate: dueDateIso,
            categoryId: this.getCategoryIdFromName(formValue.category),
            statusId: this.getStatusIdFromName(formValue.status),
            // Send base64 only if a new file is picked; send empty string otherwise
            attachment: mainAttachmentBase64,
            subTasks: await Promise.all(
                (formValue.subTasks || []).map(async (st: any, idx: number) => {
                    const picked = this.subTaskAttachmentFiles[idx] || null;
                    const subAttach = picked
                        ? await this.getFileBase64(picked)
                        : '';
                    return {
                        id: st.id ?? 0,
                        title: st.title,
                        assigneeId: this.getAssigneeIdFromName(
                            st.assignee || ''
                        ),
                        startDate: new Date(st.startDate).toISOString(),
                        dueDate: new Date(st.dueDate).toISOString(),
                        statusId: this.getStatusIdFromName(st.status || ''),
                        subTaskAttachment: subAttach,
                        description: st.description || ''
                    };
                })
            ),
            description: formValue.description || ''
        };

        this.taskService.updateTaskDetails(updateRequest).subscribe({
            next: (response) => {
                this.toastr.success(
                    response.message || 'Task updated successfully'
                );
                this.closeEditDialog();
                this.fetchTasks(); // Refresh the task list
            },
            error: (error: any) => {
                console.error('Error updating task:', error);
                this.toastr.error('Failed to update task');
            }
        });
    }

    // Helper methods for ID mapping
    private getAssigneeIdFromName(assigneeName: string): number {
        const assignee = this.assigneeOptions.find(
            (a) => a.label === assigneeName
        );
        return assignee?.value;
    }

    private getAssigneeNameById(assigneeId: number): string {
        const assignee = this.assigneeOptions.find(
            (a) => a.value === assigneeId
        );
        return assignee?.label || '';
    }

    private getCategoryIdFromName(categoryName: string): number {
        const category = this.categoryOptions.find(
            (c) => c.label === categoryName
        );
        return category?.value;
    }

    private getCategoryNameById(categoryId: number): string {
        const category = this.categoryOptions.find(
            (c) => c.value === categoryId
        );
        return category?.label || '';
    }

    private getStatusIdFromName(statusName: string): number {
        const status = this.statusOptions.find((s) => s.label === statusName);
        return status?.value || TasksListComponent.DEFAULT_STATUS_ID;
    }

    private getStatusNameById(statusId: number): string {
        const status = this.statusOptions.find((s) => s.value === statusId);
        return status?.label || '';
    }

    private getFarmIdFromName(farmName: string): number {
        const farm = this.farmOptions.find(
            (f) => (f as any).name === farmName || f.label === farmName
        );
        return (farm as any)?.value || 0;
    }

    private getHouseIdFromName(houseName: string): number {
        const house = this.houseOptions.find(
            (h) => (h as any).name === houseName || h.label === houseName
        );
        return (house as any)?.value || 0;
    }

    private getPartitionIdFromName(partitionName: string): number {
        const part = this.partitionOptions.find(
            (p) =>
                (p as any).name === partitionName || p.label === partitionName
        );
        return (part as any)?.value || 0;
    }

    private getCycleIdFromName(cycleName: string): number {
        const cycle = this.cycleOptions.find(
            (c) => (c as any).name === cycleName || c.label === cycleName
        );
        return (cycle as any)?.value || 0;
    }

    // Load cycles for edit form context (based on selected farm & house)
    private loadCyclesForEdit(farmId: number, houseId: number) {
        this.placementService
            .getCyclesByFarmAndHouse(farmId, houseId)
            .subscribe({
                next: (result) => {
                    const cycles = Array.isArray(result) ? result : [];

                    this.cycleOptions = cycles.map((cycle: any) => ({
                        value: cycle.id,
                        label: cycle.name
                    }));
                },
                error: () => {
                    this.cycleOptions = [];
                }
            });
    }

    private loadPartitionsForHouse(houseId: number) {
        this.partitionOptions = [];
        this.farmEnvironmentService
            .getdashboardFarmHousePartitionList(houseId)
            .subscribe({
                next: (result) => {
                    const parts = Array.isArray(result)
                        ? result
                        : result?.partitions ||
                          result?.partitionsList ||
                          result ||
                          [];
                    this.partitionOptions = parts.map((p: any) => ({
                        value: p.id,
                        label: p.name
                    }));
                },
                error: () => {
                    this.partitionOptions = [];
                }
            });
    }

    // File input handlers for edit dialog
    onEditFileSelected(event: any) {
        const files = Array.from(event?.target?.files || []) as File[];
        this.mainAttachmentFile = files.length ? files[0] : null;
    }

    onEditSubTaskFileSelected(event: any, index: number) {
        const files = Array.from(event?.target?.files || []) as File[];
        this.subTaskAttachmentFiles[index] = files.length ? files[0] : null;
    }

    private async getFileBase64(file: File): Promise<string | null> {
        const dataUrl = await this.fileToBase64(file);
        const commaIdx = dataUrl.indexOf(',');
        return commaIdx > -1 ? dataUrl.substring(commaIdx + 1) : dataUrl;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(file);
        });
    }

    fileNameFromUrl(url?: string | null): string {
        if (!url) return '';
        try {
            const parts = url.split('?')[0].split('#')[0].split('/');
            return parts[parts.length - 1] || url;
        } catch {
            return url;
        }
    }

    closeEditDialog() {
        this.showEditDialog = false;
        this.editTaskData = null;
        this.editTaskForm.reset();
    }

    fetchTasks() {
        this.loading = true;
        const requestId = ++this.lastRequestId;
        const fv = this.filterForm?.value || {};
        const dueDateIso: string | undefined = fv.dueDate
            ? new Date(fv.dueDate).toISOString()
            : this.dueDate
              ? new Date(this.dueDate).toISOString()
              : undefined;
        const toNum = (v: any): number | undefined => {
            if (v === null || v === undefined || v === '') return undefined;
            const n = Number(v);
            return isNaN(n) ? undefined : n;
        };
        const filter: any = {
            title: this.title || undefined,
            assigneeId: fv.assigneeId || this.assigneeId || undefined,
            // Send IDs for category and status
            categoryId: toNum(fv.category) ?? this.categoryId ?? undefined,
            statusId: toNum(fv.status) ?? this.statusId ?? undefined,
            farmId: fv.farmId || this.farmId || undefined,
            houseId: fv.houseId || this.houseId || undefined,
            partitionId: fv.partitionId || undefined,
            cycleId: fv.cycleId || this.cycleId || undefined,
            dueDate: dueDateIso,
            parentTaskId: '',
            pageNumber: this.currentPage,
            pageSize: this.pageSize
        };
        this.taskService.getTaskList(filter).subscribe({
            next: (res: any) => {
                if (requestId !== this.lastRequestId) return;
                const list = this.extractTasksFromResponse(res);
                this.tasks = list;
                const total = res?.totalCount ?? 0;
                this.totalRecords = total;
                this.loading = false;
            },
            error: () => {
                if (requestId !== this.lastRequestId) return;
                this.totalRecords = 0;
                this.loading = false;
            }
        });
    }

    // actions menu
    toggleActionsMenu(id: string) {
        this.activeActionMenu = this.activeActionMenu === id ? null : id;
    }

    @HostListener('document:click', ['$event'])
    closeActionMenuOnClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (!target.closest('.smart-actions-dropdown')) {
            this.activeActionMenu = null;
        }
    }

    private extractTasksFromResponse(res: any): any[] {
        if (Array.isArray(res?.tasks)) {
            return res.tasks;
        } else if (Array.isArray(res)) {
            return res;
        } else if (Array.isArray(res?.data)) {
            return res.data;
        } else {
            return [];
        }
    }

    onPageChange(event: any) {
        const newPageSize = Number(event?.rows) || this.pageSize;
        const newPageZeroBased =
            typeof event?.page === 'number'
                ? event.page
                : Math.floor((Number(event?.first) || 0) / newPageSize);
        this.pageSize = newPageSize;
        this.currentPage = newPageZeroBased + 1;
        this.fetchTasks();
    }
}
